import { Command } from 'commander';
import { readFileSync } from 'node:fs';
import { ApiClient } from '../client.js';
import { formatOutput } from '../output.js';
import { getFormat } from '../helpers.js';
import { confirm } from '../prompt.js';
import { requireActiveOrg } from '../require-active-org.js';

/**
 * Resolve a secret value from exactly one of `--value` (a plain string) or `--file` (the file's bytes,
 * base64-encoded — for certificates, keystores, or service-account JSON). Throws if neither or both given.
 */
function resolveValue(opts: { value?: string; file?: string }): string {
  if ((opts.value === undefined) === (opts.file === undefined)) {
    throw new Error('provide exactly one of --value or --file');
  }
  if (opts.file !== undefined) return readFileSync(opts.file).toString('base64');
  return opts.value!;
}

export const secretsCommand = new Command('secrets')
  .description('Manage organization vault secrets');

secretsCommand.command('create')
  .description('Store a vault secret (a string value, or a file via --file as base64)')
  .requiredOption('--name <name>', 'Secret name')
  .option('--value <value>', 'Secret value (plain string)')
  .option('--file <path>', 'Read the value from a file and base64-encode it (certificates, keystores, SA JSON)')
  .option('--content-type <mime>', 'MIME type of the value (e.g. application/x-pkcs12)')
  .option('--expires-at <date>', 'Expiry as ISO-8601 (e.g. 2027-01-01T00:00:00Z) — surfaced by `list --expiring`')
  .option('--tags <tags>', 'Comma-separated tags')
  .action(async function (this: Command, opts: { name: string; value?: string; file?: string; contentType?: string; expiresAt?: string; tags?: string }) {
    await requireActiveOrg();
    const client = new ApiClient();
    const body: Record<string, unknown> = { name: opts.name, value: resolveValue(opts) };
    if (opts.contentType) body.content_type = opts.contentType;
    if (opts.expiresAt) body.expires_at = opts.expiresAt;
    if (opts.tags) body.tags = opts.tags.split(',').map((t) => t.trim()).filter(Boolean);
    const data = await client.request('POST', '/v1/vault/secrets', body);
    console.log(formatOutput(data, getFormat(this)));
  });

secretsCommand.command('list')
  .description('List vault secrets (values masked)')
  .option('--expiring', 'Only secrets expiring within --days (default 30)')
  .option('--days <n>', 'Window for --expiring, in days', '30')
  .action(async function (this: Command, opts: { expiring?: boolean; days: string }) {
    const client = new ApiClient();
    const path = opts.expiring ? `/v1/vault/secrets?expiring_within_days=${encodeURIComponent(opts.days)}` : '/v1/vault/secrets';
    const data = await client.request('GET', path);
    console.log(formatOutput(data, getFormat(this)));
  });

secretsCommand.command('get <id>')
  .description('Get vault secret metadata (value masked)')
  .action(async function (this: Command, id: string) {
    const client = new ApiClient();
    const data = await client.request('GET', `/v1/vault/secrets/${id}`);
    console.log(formatOutput(data, getFormat(this)));
  });

secretsCommand.command('rotate <id>')
  .description('Rotate a secret by installing a new caller-supplied value (from --value or --file)')
  .option('--value <value>', 'New secret value (plain string)')
  .option('--file <path>', 'Read the new value from a file and base64-encode it')
  .option('--expires-at <date>', "Set the rotated credential's new expiry (ISO-8601)")
  .action(async function (this: Command, id: string, opts: { value?: string; file?: string; expiresAt?: string }) {
    const client = new ApiClient();
    const body: Record<string, unknown> = { value: resolveValue(opts) };
    if (opts.expiresAt) body.expires_at = opts.expiresAt;
    const data = await client.request('POST', `/v1/vault/secrets/${id}/rotate`, body);
    console.log(formatOutput(data, getFormat(this)));
  });

secretsCommand.command('delete <id>')
  .description('Delete a vault secret')
  .option('-y, --yes', 'Skip confirmation prompt')
  .action(async function (this: Command, id: string, opts: { yes?: boolean }) {
    if (!opts.yes && !(await confirm(`Delete vault secret ${id}? This cannot be undone.`))) {
      console.log('Aborted');
      return;
    }
    const client = new ApiClient();
    await client.request('DELETE', `/v1/vault/secrets/${id}`);
    console.log('Secret deleted');
  });
